import { Injectable } from "@angular/core";
import L from "../leaflet-plugins-loader";
import { ILeafletInstance } from "../leaflet.interface";
import { IMapWidgetFormGroupSettings } from "../../../map-widget-settings/map-widget-settings.interface";
import { INgsiMapBaseEntity } from "../../ngsild/interfaces/ngsi-entity.interface";
import { INgsiAIS } from "../../ngsild/interfaces/ngsi-ais.interface";
import { INgsiMaritimeTargets } from "../../ngsild/interfaces/ngsi-maritime-targets.interface";
import { INgsiRadarTarget } from "../../ngsild/interfaces/ngsi-radar-target.interface";
import { INgsiFusionMaritimeTarget } from "../../ngsild/interfaces/ngsi-fusion-maritime-target.interface";
import { LeafletService } from "../leaflet.service";
import { TrackerDetails } from "./leaflet-tracksymbol2/ais-track-symbol";
import { PopupService } from "../popup/popup.service";
import { TooltipService } from "../tooltips/tooltip.service";
import * as AisTrackSymbol from "./leaflet-tracksymbol2/ais-track-symbol";
import * as NgsiAIS from "../../ngsild/models/ngsi-ais";
import * as NgsiMaritimeTargets from "../../ngsild/models/ngsi-maritime-targets";
import * as NgsiRadarTarget from "../../ngsild/models/ngsi-radar-target";
import * as NgsildConstants from "../../ngsild/helpers/ngsild.constants";
import * as leafletInstanceConfig from "../../ngsild/helpers/leaflet-instance-config.constants";
import * as NgsiFusionMaritimeTarget from "../../ngsild/models/ngsi-fusion-maritime-target";

@Injectable({
  providedIn: "root",
})
export class MarkerService {
  constructor(
    public leafletService: LeafletService,
    public popupService: PopupService,
    public tooltipService: TooltipService,
  ) {}

  addOrUpdateMarker(
    appPluginWidgetId: string,
    entity: INgsiMapBaseEntity,
    settings: IMapWidgetFormGroupSettings,
    instance: ILeafletInstance,
    isTemporary: boolean = false,
  ): void {
    if (!isTemporary) {
      this.addOrUpdateMapMarker(appPluginWidgetId, entity, settings, instance);
    } else {
      this.addOrUpdateTemporaryMarker(
        appPluginWidgetId,
        entity,
        settings,
        instance,
      );
    }
  }

  addOrUpdateMapMarker(
    appPluginWidgetId: string,
    entity: INgsiMapBaseEntity,
    settings: IMapWidgetFormGroupSettings,
    instance: ILeafletInstance,
  ): void {
    let marker = instance.markers.get(entity.id);
    if (marker) {
      marker.mtw = entity["mtw"];
      marker.windDirTrue = entity["windDirTrue"];
      marker.windSpeedM = entity["windSpeedM"];
      marker.depthMeters = entity["depthMeters"];
      this.updateMarker(entity, marker, settings, instance);
      this.leafletService.addMarkerToLayer(
        appPluginWidgetId,
        entity.type,
        marker,
        true,
        instance,
      );
    } else {
      instance.entities.set(entity.id, entity);
      this.leafletService.addLayerByType(
        appPluginWidgetId,
        entity.type,
        instance,
      );

      marker = this.createMarker(entity, settings, instance);
      if (marker) {
        marker.ngsiEntityId = entity.id;
        marker.mtw = entity["mtw"];
        marker.windDirTrue = entity["windDirTrue"];
        marker.windSpeedM = entity["windSpeedM"];
        marker.depthMeters = entity["depthMeters"];
        this.listenOnMarkerEvents(marker, instance);
        instance.markers.set(entity.id, marker);
        this.leafletService.addMarkerToLayer(
          appPluginWidgetId,
          entity.type,
          marker,
          true,
          instance,
        );
      }
    }
  }

  addOrUpdateTemporaryMarker(
    appPluginWidgetId: string,
    entity: INgsiMapBaseEntity,
    settings: IMapWidgetFormGroupSettings,
    instance: ILeafletInstance,
  ): void {
    const isExclude = this.leafletService.excludeFromTemporaryEntities(
      <INgsiMaritimeTargets>entity,
      settings,
      instance,
    );
    if (isExclude) {
      console.log(`Excluded entity from temporary entities: ${entity.id}.`);
      return;
    }

    const showLayerOnMap = NgsildConstants.showUpdateTempDataOnMap(
      entity,
      instance,
    );

    let marker = instance.markers.get(entity.id);

    instance.additionalEntities.set(entity.id, entity);

    const layerType =
      instance.dataConfig.dataModel.AIS.type === entity.type
        ? `${instance.dataConfig.dataModel.AIS.type}${instance.dataConfig.dataModel.AIS.tempLayerSuffix}`
        : entity.type;

    if (marker) {
      this.leafletService.addTemporaryEntities(
        entity.id,
        entity.type,
        instance,
      );

      this.updateMarker(entity, marker, settings, instance);

      this.leafletService.addMarkerToLayer(
        appPluginWidgetId,
        layerType,
        marker,
        showLayerOnMap,
        instance,
      );
    } else {
      // includes also temporary entities
      this.leafletService.addTemporaryEntities(
        entity.id,
        entity.type,
        instance,
      );
      if (showLayerOnMap) {
        this.leafletService.addLayerByType(
          appPluginWidgetId,
          layerType,
          instance,
        );
      }

      marker = this.createMarker(entity, settings, instance);
      if (marker) {
        instance.markers.set(entity.id, marker);
        this.leafletService.addMarkerToLayer(
          appPluginWidgetId,
          layerType,
          marker,
          showLayerOnMap,
          instance,
        );
      }
    }
  }

  private createMarker(
    entity: INgsiMapBaseEntity,
    settings: IMapWidgetFormGroupSettings,
    instance: ILeafletInstance,
  ) {
    const { config } = leafletInstanceConfig;
    const { dataSourceSelector } = config;
    const commonArgs = [
      settings,
      instance,
      this.popupService,
      this.tooltipService,
      this,
    ] as const;

    switch (entity.type) {
      case dataSourceSelector.MMSI.value.type:
        if (settings.dataSourceIdentifier === dataSourceSelector.MMSI.label) {
          return NgsiAIS.createMarker(entity as INgsiAIS, ...commonArgs);
        }
        break;

      case NgsiMaritimeTargets.entityType:
        return NgsiMaritimeTargets.createMarker(
          entity as INgsiMaritimeTargets,
          ...commonArgs,
        );

      case NgsiRadarTarget.entityType:
        return NgsiRadarTarget.createMarker(
          entity as INgsiRadarTarget,
          ...commonArgs,
        );

      case NgsiFusionMaritimeTarget.entityType:
        return NgsiFusionMaritimeTarget.createMarker(
          entity as INgsiFusionMaritimeTarget,
          ...commonArgs,
        );
    }

    return undefined;
  }

  private updateMarker(
    entity: INgsiMapBaseEntity,
    marker: L.Marker<any>,
    settings: IMapWidgetFormGroupSettings,
    instance: ILeafletInstance,
  ): void {
    const { config } = leafletInstanceConfig;
    const { dataSourceSelector } = config;
    const commonArgs = [
      marker,
      settings,
      instance,
      this.popupService,
      this.tooltipService,
      this,
    ] as const;

    switch (entity.type) {
      case dataSourceSelector.MMSI.value.type:
        if (settings.dataSourceIdentifier === dataSourceSelector.MMSI.label) {
          NgsiAIS.updateMarker(entity as INgsiAIS, ...commonArgs);
          return;
        }
        break;

      case NgsiMaritimeTargets.entityType:
        NgsiMaritimeTargets.updateMarker(
          entity as INgsiMaritimeTargets,
          ...commonArgs,
        );
        return;

      case NgsiRadarTarget.entityType:
        NgsiRadarTarget.updateMarker(entity as INgsiRadarTarget, ...commonArgs);
        return;

      case NgsiFusionMaritimeTarget.entityType:
        NgsiFusionMaritimeTarget.updateMarker(
          entity as INgsiFusionMaritimeTarget,
          ...commonArgs,
        );
        return;
    }
  }

  createDivIconMarkerContent(
    entity: INgsiMaritimeTargets,
    settings: IMapWidgetFormGroupSettings,
    instance: ILeafletInstance,
    icon: L.DivIcon,
  ) {
    const marker = L.marker(
      [entity.location.coordinates[1], entity.location.coordinates[0]],
      { icon },
    );
    const positionReport = AisTrackSymbol.setAISPositionReport(
      <INgsiAIS>(<unknown>entity),
    );
    const options = AisTrackSymbol.setAISReportOptions(
      <INgsiAIS>(<unknown>entity),
    );

    const trackerDetails: TrackerDetails = {
      marker: marker,
      positionReport: positionReport,
      options: options,
    };

    return trackerDetails;
  }

  updateDivIconMarkerContent(
    entity: INgsiMaritimeTargets,
    marker: L.Marker<any>,
    settings: IMapWidgetFormGroupSettings,
    instance: ILeafletInstance,
  ) {
    const positionReport = AisTrackSymbol.setAISPositionReport(
      <INgsiAIS>(<unknown>entity),
    );
    const options = AisTrackSymbol.setAISReportOptions(
      <INgsiAIS>(<unknown>entity),
    );

    const trackerDetails = {
      marker: marker,
      positionReport: positionReport,
      options: options,
    };

    marker.setLatLng([
      entity.location.coordinates[1],
      entity.location.coordinates[0],
    ]);

    return trackerDetails;
  }

  removeMarker(entityId: string, instance: ILeafletInstance): void {
    const marker = instance.markers.get(entityId);

    if (marker) {
      instance.map.removeLayer(marker);
      instance.markers.delete(entityId);
    }
  }

  listenOnMarkerEvents(marker: L.Marker, instance: ILeafletInstance) {
    marker.on("add", (event: any) => {
      requestAnimationFrame(() => {
        instance.map.fire("updatingObcNavigationsInstruments", event);
      });
    });

    marker.on("move", (event: any) => {
      requestAnimationFrame(() => {
        instance.map.fire("updatingObcNavigationsInstruments", event);
      });
    });
  }
}
